import { motion } from "framer-motion";
import { ArrowUpRight, Facebook, Instagram, Mail } from "lucide-react";
import { playTick } from "@/components/site/Nav";

const FOUNDERS = ["Alex de Selve", "Antoine Eole"];

const SOCIALS = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/gentle.and.monster/",
    icon: Instagram,
    testid: "contact-social-instagram",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61595264244978",
    icon: Facebook,
    testid: "contact-social-facebook",
  },
];

export default function Contact({ onOpenConcierge }) {
  return (
    <section id="contact" data-testid="section-contact" className="border-t border-[#27272A] px-6 py-28 sm:px-10 sm:py-36 lg:px-14">
      <p className="mb-14 font-monoalt text-[13px] font-light uppercase tracking-[0.18em] text-zinc-500">
        /// Contact
      </p>

      <motion.button
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        type="button"
        onClick={onOpenConcierge}
        data-testid="contact-cta"
        className="group block w-full cursor-pointer text-left"
      >
        <span className="flex flex-wrap items-center gap-x-6 font-display text-[15vw] font-extrabold uppercase leading-[0.9] tracking-tighter sm:text-[9vw]">
          Let&apos;s Talk
          <ArrowUpRight
            className="h-[0.7em] w-[0.7em] transition-transform duration-500 group-hover:-translate-y-3 group-hover:translate-x-3"
            strokeWidth={1.5}
          />
        </span>
        <span className="mt-6 inline-flex items-center gap-3 text-sm text-zinc-400 transition-colors duration-300 group-hover:text-white" data-testid="contact-email">
          <Mail className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
          hello@gentleandmonster.com
        </span>
      </motion.button>

      <a
        href="https://wa.me/33699966024"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="contact-whatsapp"
        className="mt-3 inline-flex items-center gap-3 text-sm text-zinc-400 transition-colors duration-300 hover:text-white"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 shrink-0" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        +33 6 99 96 60 24
      </a>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.3 }}
        className="mt-20 flex flex-wrap items-end justify-between gap-10 border-t border-[#27272A] pt-10"
      >
        <div className="flex flex-wrap gap-x-14 gap-y-4">
          {FOUNDERS.map((f, i) => (
            <div key={f} data-testid={`contact-founder-${i + 1}`}>
              <p className="font-display text-lg font-bold uppercase tracking-tight">{f}</p>
              <p className="mt-1 font-monoalt text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                Co-founder
              </p>
            </div>
          ))}
        </div>
        <div data-testid="contact-socials">
          <p className="mb-3 font-monoalt text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            Socials
          </p>
          <div className="flex gap-[18px]">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={s.testid}
                aria-label={`GENTLE & MONSTER on ${s.name} (opens in a new tab)`}
                onPointerEnter={playTick}
                className="social-tick group relative flex h-[66px] w-[66px] items-center justify-center"
              >
                <img
                  src="/assets/logo-duo.png"
                  alt=""
                  aria-hidden="true"
                  className="h-[28px] w-auto opacity-[0.55] transition-opacity duration-500 group-hover:opacity-80"
                />
                <s.icon
                  className="social-blink absolute bottom-1.5 right-1.5 h-6 w-6 text-zinc-200 transition-colors duration-500 group-hover:text-white"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              </a>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
